import { useCallback, useEffect, useRef, useState } from "react";
import type { FlowKind, KratosFlow, SubmitOutcome } from "@/lib/kratos";
import {
  createFlow,
  getFlow,
  KratosFlowExpiredError,
  kratosErrorCopy,
  submitFlow,
} from "@/lib/kratos";

export type UseKratosFlowOptions = {
  /** Adopt an already-fetched flow (e.g. registration `continue_with`)
   * instead of creating one. */
  initialFlow?: KratosFlow;
  /** Resume an existing flow (deep link `?flow=`) instead of creating
   * a fresh one. Falls back to a fresh flow when expired/foreign. */
  flowId?: string;
  /** Extra query params for flow creation (`refresh`, `return_to`, ...). */
  createParams?: Record<string, string>;
  /** Skip flow creation/fetch entirely. Used when the panel only resumes a
   * session (e.g. an OIDC `?social=1` return that goes straight to exchange):
   * creating a login flow then would 400 `session_already_available` and
   * flash that error before the exchange completes. */
  skip?: boolean;
  /** Called once per terminal success (login/registration completed). */
  onSuccess?: (outcome: Extract<SubmitOutcome, { kind: "success" }>) => void | Promise<void>;
  /** Called when a submit hands back a *different* flow kind (e.g.
   * registration → verification via continue_with/422). */
  onFlowChange?: (flow: KratosFlow, flowKind: FlowKind) => void;
};

/** Lifecycle for one Kratos self-service flow: create (or resume, or
 * `skip`) on mount, submit with transparent re-create on expiry, keep the
 * latest flow (with its validation messages) in state. */
export function useKratosFlow(kind: FlowKind, options: UseKratosFlowOptions = {}) {
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Options live in a ref so flow creation doesn't re-run when callers
  // pass inline callbacks. `flowId` is the exception — see below.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // `flowId` IS a reactive dep (read directly, not via the ref): navigating
  // from `?flow=<id>` to a clean route must drop the resumed flow and create a
  // fresh one. Without this, the stale flow lingers and the panel never leaves
  // the resumed state — e.g. the OIDC re-link prompt's "use another method"
  // would navigate but show the same prompt.
  const flowId = options.flowId;

  const initialize = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { initialFlow, createParams, skip } = optionsRef.current;
      if (skip) {
        // No flow needed — the caller is only resuming a session.
        setFlow(null);
        return;
      }
      if (initialFlow) {
        setFlow(initialFlow);
        return;
      }
      if (flowId) {
        try {
          setFlow(await getFlow(kind, flowId));
          return;
        } catch (err) {
          if (!(err instanceof KratosFlowExpiredError)) throw err;
          // Expired deep link — fall through to a fresh flow.
        }
      }
      setFlow(await createFlow(kind, createParams));
    } catch (err) {
      setError(kratosErrorCopy(err));
    } finally {
      setLoading(false);
    }
  }, [kind, flowId]);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const submit = useCallback(
    async (values: Record<string, unknown>): Promise<SubmitOutcome | null> => {
      if (!flow) return null;
      setSubmitting(true);
      setError(null);
      try {
        let outcome: SubmitOutcome;
        try {
          outcome = await submitFlow(kind, flow, values);
        } catch (err) {
          if (!(err instanceof KratosFlowExpiredError)) throw err;
          // Transparent expiry recovery: fresh flow (fresh CSRF), one retry.
          const fresh = await createFlow(kind, optionsRef.current.createParams);
          setFlow(fresh);
          outcome = await submitFlow(kind, fresh, values);
        }
        if (outcome.kind === "success") {
          await optionsRef.current.onSuccess?.(outcome);
          return outcome;
        }
        setFlow(outcome.flow);
        if (outcome.flowKind !== kind) {
          optionsRef.current.onFlowChange?.(outcome.flow, outcome.flowKind);
        }
        return outcome;
      } catch (err) {
        setError(kratosErrorCopy(err));
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [flow, kind],
  );

  return {
    flow,
    loading,
    submitting,
    /** Hard client/transport error copy — per-field validation lives on
     * the flow itself (`nodeMessages` / `firstFlowError`). */
    error,
    setError,
    submit,
    restart: initialize,
  };
}
